import { Box, Flex, Spinner } from "@chakra-ui/react";
import { lazy, Suspense, useMemo, useState, useEffect } from "react";
import useRooms from "./Hooks/useRooms";
import ChatErrorBoundary from "./Components/ErrorBoundary";
import ReduxDataSourceProvider from "./Data/ReduxDataSourceProvider";
import { ChatAppProvider } from "./AppContext";
import { ChatStateProvider, useChatState } from "./StateContext";
import { getTranslations } from "../../utils/lang";

const DeleteRoomModel = lazy(
  () => import("./Components/Sidebar/Models/Delete"),
);
const ChatWindow = lazy(() => import("./Components/Conversation"));
const ChatSidebar = lazy(() => import("./Components/Sidebar"));

function ChatPageContent({ Socket, Lang, texts }) {
  const [ShowSidebar, SetShowSidebar] = useState(true);
  const { selectedRoom } = useChatState();
  const SidebarWidth = 28;

  const {
    OnSelectRoom,
    OnModelToggle,
    IsModelOpen,
    IsDeleting,
    IsFavoring,
    IsLoading,
    OnRefetch,
    OnSubmit,
    OnChange,
    OnCreate,
    OnClose,
    SetPage,
    State,
    Rooms,
    Total,
    Page,
  } = useRooms({ Socket, Lang });

  return (
    <>
      <Suspense
        fallback={
          <Flex
            align="center"
            justify="center"
            h="calc(var(--chakra-vh) - 3rem)"
          >
            <Spinner />
          </Flex>
        }
      >
        <Flex h="calc(100dvh - 3.1rem)"
          className="vvvvv-3"
          
        overflow="hidden" position="relative">
          <Box
            w={ShowSidebar ? `${SidebarWidth}%` : "0%"}
            minW={ShowSidebar ? `${SidebarWidth}%` : "0%"}
            maxW={ShowSidebar ? `${SidebarWidth}%` : "0%"}
            borderRight={ShowSidebar ? "1px solid" : "none"}
            borderColor="black"
            overflow="hidden"
            className="Chat-sidebar"
            transition="width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease, border-color 0.2s ease"
            pointerEvents={ShowSidebar ? "auto" : "none"}
            opacity={ShowSidebar ? 1 : 0}
            bg="white"
          >
            {ShowSidebar && (
              <ChatErrorBoundary Lang={Lang} onRetry={OnRefetch}>
                <ChatSidebar
                  OnModelToggle={OnModelToggle}
                  onSelectRoom={OnSelectRoom}
                  IsLoading={IsLoading}
                  OnRefetch={OnRefetch}
                  OnCreate={OnCreate}
                  OnChange={OnChange}
                  SetPage={SetPage}
                  Socket={Socket}
                  rooms={Rooms}
                  Total={Total}
                  Lang={Lang}
                  texts={texts}
                  Page={Page}
                />
              </ChatErrorBoundary>
            )}
          </Box>

          <Box flex={1}
          className="vvvvv-4"
          bg="white"
          minW={0}
          overflow="hidden">
            <ChatErrorBoundary Lang={Lang} onRetry={OnRefetch}>
              <ChatWindow
                OnToggleSidebar={() => SetShowSidebar((prev) => !prev)}
                OnSubmit={OnSubmit}
                Socket={Socket}
                room={selectedRoom}
                Lang={Lang}
                texts={texts}
              />
            </ChatErrorBoundary>
          </Box>
        </Flex>
      </Suspense>

      <Suspense fallback={null}>
        {["Favorite", "UnFavorite"].includes(State.Tab) ? (
          <DeleteRoomModel
            OnSubmit={OnSubmit}
            OnClose={OnClose}
            IsLoading={IsDeleting || IsFavoring}
            IsOpen={IsModelOpen}
            State={State}
            Lang={Lang}
          />
        ) : null}
      </Suspense>
    </>
  );
}

function ChatPage({
  Socket,
  CurrentUser: CurrentUserProp = null,
  Lang: LangProp = null,
  InitialRoom = null,
  InitialFilter = null,
  LayoutComponent = null,
  ...layoutProps
}) {
  const [persistedUser, setPersistedUser] = useState(null);
  const [langFromStorage, setLangFromStorage] = useState(LangProp || "en");

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        setPersistedUser(JSON.parse(user));
      } catch (e) {
        console.error("Failed to parse user from localStorage", e);
      }
    }
    if (!LangProp) {
      const savedLang = localStorage.getItem("lang");
      if (savedLang) {
        setLangFromStorage(savedLang);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Lang = LangProp || langFromStorage;
  const texts = useMemo(() => getTranslations(Lang), [Lang]);
  const resolvedUserSource = CurrentUserProp || persistedUser || null;
  const isRtl = Lang === "ar";
  const CurrentUser = useMemo(() => {
    const source = resolvedUserSource || {};
    const roleData = source?.UserRole || {};
    const subSectionData = {};
    return {
      Id: source?.CustomerId ?? source?.Id ?? null,
      Name: source?.CustomerName ?? source?.Name ?? "",
      Type: source ? "Customer" : null,
      AccessToken: source?.CustomerAccessToken ?? source?.AccessToken ?? "",
      RoleKey: roleData?.RoleKey ?? source?.RoleKey ?? null,
      SubSectionKey:
        subSectionData?.SubSectionKey ??
        source?.SubSectionKey ??
        null,
      UserRole: roleData,
      UserSubSection: subSectionData,
    };
  }, [resolvedUserSource]);
  const AppContextValue = useMemo(
    () => ({
      CurrentUser,
      Lang,
      IsRtl: isRtl,
    }),
    [CurrentUser, Lang, isRtl],
  );

  const initialRoomValue = InitialRoom || null;
  const initialFilterValue = {
    ...(InitialFilter || {}),
  };

  const content = (
    <ChatErrorBoundary Lang={Lang} onRetry={() => window.location.reload()}>
      <ReduxDataSourceProvider>
        <ChatAppProvider value={AppContextValue}>
          <ChatStateProvider
            initialRoom={initialRoomValue}
            initialFilter={initialFilterValue}
          >
            <ChatPageContent Socket={Socket} Lang={Lang} texts={texts} />
          </ChatStateProvider>
        </ChatAppProvider>
      </ReduxDataSourceProvider>
    </ChatErrorBoundary>
  );

  if (LayoutComponent) {
    return (
      <LayoutComponent {...layoutProps} p={0.5}>
        {content}
      </LayoutComponent>
    );
  }

  return (
    <Box 
    className="vvvvv-2"
    h="100%" p={0}>
      {content}
    </Box>
  );
}

export default ChatPage;
